import posthog from "posthog-js"
import type { Properties } from "posthog-js"
import {
  MAX_EXCEPTIONS_PER_PAGE_LOAD,
  isErrorTrackingEnabled,
} from "./exceptions"

type PendingError = {
  error: unknown
  properties?: Properties
}

// Error boundaries fire in React's commit phase, before PostHogProvider's init
// effect runs, so a crash on a page's first render arrives before PostHog has
// loaded. Hold those errors until init instead of dropping them.
const pendingErrors: PendingError[] = []

/**
 * Report a caught error. Errors raised before PostHog loads are queued and
 * sent by `flushPendingErrors`. While the kill switch is off nothing is sent.
 * Anything not sent immediately is logged, so it still leaves a trace in the
 * browser console even if PostHog never loads.
 */
export function captureError(error: unknown, properties?: Properties): void {
  if (!isErrorTrackingEnabled()) {
    console.error(error)

    return
  }

  if (!posthog.__loaded) {
    console.error(error)

    if (pendingErrors.length < MAX_EXCEPTIONS_PER_PAGE_LOAD) {
      pendingErrors.push({ error, properties })
    }

    return
  }

  posthog.captureException(error, properties)
}

/** Send errors queued before PostHog loaded. Call right after `posthog.init`. */
export function flushPendingErrors(): void {
  if (!posthog.__loaded) {
    return
  }

  for (const { error, properties } of pendingErrors.splice(0)) {
    posthog.captureException(error, properties)
  }
}
